import api from "../../services/baseapi";
import { normalizeRole, atLeast, canDeleteProject } from "../../utils/roles";

// ─────────────────────────────────────────────────────────────────────────────
// Reporting module — API client + types
// Mirrors docs: reporting_api.md (base prefix `/reporting`).
// ─────────────────────────────────────────────────────────────────────────────

// ── Blocks ──────────────────────────────────────────────────────────────────

export type BlockType = "text" | "metric" | "chart" | "table" | "ai_narrative";

export type SourceFamily =
  | "formula"
  | "scorecard"
  | "dashboard"
  | "doc_insights";

export interface BlockSource {
  family: SourceFamily;
  id: string | null;
}

/** `config` shape varies by block type (see §2.1 of the guide). Kept loose on
 * purpose — helpers below build the correct shape per type. */
export interface BlockConfig {
  // text
  body?: string;
  // metric / chart / table
  source?: BlockSource;
  target?: number | null;
  chart_type?: string;
  limit?: number;
  // ai_narrative
  scope?: string[];
  tone?: string;
  length?: "Short" | "Medium" | "Long";
  note?: string;
  [key: string]: any;
}

// ── Resolved (frozen) data per block type (§2.2) ──────────────────────────────

export interface MetricItem {
  label: string;
  value: number | null;
  unit?: string | null;
  target?: number | null;
  delta?: number | null;
  direction?: "up" | "down" | "flat" | null;
}
export interface MetricData {
  items: MetricItem[];
}
export interface ChartData {
  chart_type?: string;
  title?: string;
  labels: string[];
  series: Array<{ name: string; values: number[] }>;
}
export interface TableData {
  columns: string[];
  rows: Array<Array<string | number>>;
}
export interface TextData {
  body: string;
}
export interface NarrativeData {
  text: string;
  paragraphs: string[];
  source: "ai" | "fallback";
}

export type BlockData =
  | MetricData
  | ChartData
  | TableData
  | TextData
  | NarrativeData
  | Record<string, any>;

export interface ReportBlock {
  id: string;
  type: BlockType;
  order: number;
  title: string;
  config: BlockConfig;
  /** Present only after generation (in a report's `content.blocks`). */
  data?: BlockData;
}

// ── Templates ─────────────────────────────────────────────────────────────────

export interface StandardTemplate {
  key: string;
  name: string;
  description: string;
  blocks: ReportBlock[];
}

export type TemplateKind = "custom" | "standard";

export interface ReportTemplate {
  id: string;
  project_id: string;
  name: string;
  description: string | null;
  kind: TemplateKind;
  standard_key: string | null;
  blocks: ReportBlock[];
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplatePayload {
  name: string;
  description?: string;
  blocks: ReportBlock[];
}

export interface UpdateTemplatePayload {
  name?: string;
  description?: string;
  blocks?: ReportBlock[];
}

// ── Reports ───────────────────────────────────────────────────────────────────

export type ReportStatus = "draft" | "final";

export interface ReportFacts {
  metrics?: any[];
  charts?: any[];
  tables?: any[];
  [key: string]: any;
}

export interface ReportContent {
  name: string;
  generated_at: string;
  facts: ReportFacts;
  blocks: ReportBlock[];
}

export interface Report {
  id: string;
  project_id: string;
  template_id: string | null;
  name: string;
  status: ReportStatus;
  generated_at: string;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  content: ReportContent;
}

/** Lightweight row from the list endpoint (no `content`). */
export interface ReportListItem {
  id: string;
  name: string;
  status: ReportStatus;
  template_id: string | null;
  generated_at: string;
  created_by: number | null;
  created_at: string;
}

export interface GenerateReportPayload {
  name: string;
  /** Use a saved template … */
  template_id?: string;
  /** … OR ad-hoc inline blocks. One of the two is required. */
  structure?: ReportBlock[];
}

export interface UpdateReportPayload {
  name?: string;
  /** Partial edits keyed by block `id`; only the fields you send change. */
  blocks?: Array<{ id: string; title?: string; data?: Partial<BlockData> }>;
}

// ── Roles (§1) ────────────────────────────────────────────────────────────────
// Uses the app's canonical 5-role model (owner ⊇ manager ⊇ analyst ⊇ contributor
// ⊇ viewer). `normalizeRole` also maps the old backend names, so passing a raw
// `role_name` (e.g. "Owner", "Admin", "Technical Expert") works either way.
//
// Guide §1 tiers → canonical:
//   viewer  (list/read/export)            → everyone (any signed-in role)
//   analyst (create/generate/finalize)    → analyst and up (owner/manager/analyst)
//   admin   (delete)                      → owner/manager

/** Can create/edit templates, generate, edit & regenerate reports, finalize. */
export const canAnalyst = (role: string | null): boolean =>
  atLeast(normalizeRole(role), "analyst");

/** Can delete reports and templates. */
export const canDelete = (role: string | null): boolean =>
  canDeleteProject(normalizeRole(role));

// ─────────────────────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────────────────────

const BASE = "/reporting";
const PROJ = (projectId: string) => `${BASE}/projects/${projectId}`;

/** Friendly project name for the builder header (falls back to null on error). */
export const getProjectName = async (
  projectId: string,
): Promise<string | null> => {
  try {
    const { data } = await api.get(`/projects/${projectId}`);
    return data?.name ?? null;
  } catch {
    return null;
  }
};

// ── Templates ─────────────────────────────────────────────────────────────────

export const listStandardTemplates = async (): Promise<StandardTemplate[]> => {
  const { data } = await api.get(`${BASE}/report-templates/standard`);
  return data?.standard_templates ?? [];
};

export const listTemplates = async (
  projectId: string,
): Promise<ReportTemplate[]> => {
  const { data } = await api.get(`${PROJ(projectId)}/report-templates`);
  return data?.templates ?? [];
};

export const createTemplate = async (
  projectId: string,
  payload: CreateTemplatePayload,
): Promise<ReportTemplate> => {
  const { data } = await api.post(
    `${PROJ(projectId)}/report-templates`,
    payload,
  );
  return data;
};

export const getTemplate = async (
  templateId: string,
): Promise<ReportTemplate> => {
  const { data } = await api.get(`${BASE}/report-templates/${templateId}`);
  return data;
};

export const updateTemplate = async (
  templateId: string,
  payload: UpdateTemplatePayload,
): Promise<ReportTemplate> => {
  const { data } = await api.put(
    `${BASE}/report-templates/${templateId}`,
    payload,
  );
  return data;
};

export const duplicateTemplate = async (
  templateId: string,
  projectId: string,
): Promise<ReportTemplate> => {
  const { data } = await api.post(
    `${BASE}/report-templates/${templateId}/duplicate`,
    null,
    { params: { project_id: projectId } },
  );
  return data;
};

export const deleteTemplate = async (templateId: string): Promise<void> => {
  await api.delete(`${BASE}/report-templates/${templateId}`);
};

// ── Reports ───────────────────────────────────────────────────────────────────

export const generateReport = async (
  projectId: string,
  payload: GenerateReportPayload,
): Promise<Report> => {
  const { data } = await api.post(
    `${PROJ(projectId)}/reports/generate`,
    payload,
  );
  return data;
};

export const listReports = async (
  projectId: string,
): Promise<ReportListItem[]> => {
  const { data } = await api.get(`${PROJ(projectId)}/reports`);
  return data?.reports ?? [];
};

export const getReport = async (reportId: string): Promise<Report> => {
  const { data } = await api.get(`${BASE}/reports/${reportId}`);
  return data;
};

export const updateReport = async (
  reportId: string,
  payload: UpdateReportPayload,
): Promise<Report> => {
  const { data } = await api.put(`${BASE}/reports/${reportId}`, payload);
  return data;
};

export const regenerateBlock = async (
  reportId: string,
  blockId: string,
): Promise<Report> => {
  const { data } = await api.post(
    `${BASE}/reports/${reportId}/blocks/${blockId}/regenerate`,
  );
  return data;
};

export const finalizeReport = async (reportId: string): Promise<Report> => {
  const { data } = await api.post(`${BASE}/reports/${reportId}/finalize`);
  return data;
};

export const deleteReport = async (reportId: string): Promise<void> => {
  await api.delete(`${BASE}/reports/${reportId}`);
};

/** Export is a binary stream — fetch as blob and trigger a browser download. */
export const exportReport = async (
  reportId: string,
  format: "pdf" | "docx" = "pdf",
  fallbackName = "report",
): Promise<void> => {
  const { data, headers } = await api.get(
    `${BASE}/reports/${reportId}/export`,
    { params: { format }, responseType: "blob" },
  );
  const cd: string = headers["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i.exec(cd);
  const filename = match
    ? decodeURIComponent(match[2])
    : `${fallbackName}.${format}`;
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
